import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Send, Mail, CheckCircle, Clock, MessageSquare, Trash2, Search, X } from 'lucide-react';
import TranslateButton from '@/components/TranslateButton';
import { EmojiPickerButton, EmojiReactions } from '@/components/EmojiPicker';
import { FileSelector, AttachmentsList } from '@/components/FileUpload';
import { SpeechToTextIcon } from '@/components/SpeechToText';
import { Pagination } from '@/components/Pagination';
import { useSearchParams } from 'react-router-dom';
import { DirectMessages } from '@/components/DirectMessages';

const MessagesPage = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'direct' ? 'direct' : 'support');
  const currentUserId = (() => { try { return JSON.parse(localStorage.getItem('user'))?.id; } catch { return null; } })();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Filter messages based on search query
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages;
    
    const query = searchQuery.toLowerCase();
    return messages.filter(msg => 
      msg.subject?.toLowerCase().includes(query) ||
      msg.message?.toLowerCase().includes(query) ||
      msg.admin_reply?.toLowerCase().includes(query)
    );
  }, [messages, searchQuery]);

  const [msgPage, setMsgPage] = useState(1);
  const [msgTotalPages, setMsgTotalPages] = useState(1);
  const [msgTotal, setMsgTotal] = useState(0);

  useEffect(() => {
    fetchMessages();
  }, [msgPage]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/?page=${msgPage}&per_page=25`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setMsgTotalPages(response.data.total_pages || 1);
      setMsgTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.subject || !newMessage.message) {
      toast.error('Please fill in subject and message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/messages/`, newMessage, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Upload files if any were selected
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('files', file);
        });

        await axios.post(
          `${API}/messages/${response.data.id}/attachments`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      toast.success('Message sent to admin!');
      setNewMessage({ subject: '', message: '', priority: 'normal' });
      setSelectedFiles([]);
      setShowNewMessage(false);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/messages/${messageId}/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Message deleted successfully');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleReactToMessage = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/${messageId}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMessages(); // Refresh to show updated reactions
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage({ ...newMessage, message: newMessage.message + emoji });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pending
        </span>;
      case 'replied':
        return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Replied
        </span>;
      case 'resolved':
        return <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Resolved
        </span>;
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded">High Priority</span>;
    }
    if (priority === 'urgent') {
      return <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Urgent</span>;
    }
    return null;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-5xl font-bold text-white mb-2">Messages</h1>
            <p className="text-gray-300">{activeTab === 'direct' ? 'Chat with your connections' : 'Contact the admin team'}</p>
          </div>
          {activeTab === 'support' && (
            <Button
              onClick={() => setShowNewMessage(!showNewMessage)}
              className="bg-gradient-to-r from-cyan-500 to-purple-500"
            >
              <Send className="w-4 h-4 mr-2" />
              New Message
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'support' ? 'default' : 'outline'}
            onClick={() => setActiveTab('support')}
            data-testid="messages-tab-support"
            className={activeTab === 'support' ? 'bg-gradient-to-r from-cyan-500 to-purple-500' : ''}
          >
            Support
          </Button>
          <Button
            variant={activeTab === 'direct' ? 'default' : 'outline'}
            onClick={() => setActiveTab('direct')}
            data-testid="messages-tab-direct"
            className={activeTab === 'direct' ? 'bg-gradient-to-r from-cyan-500 to-purple-500' : ''}
          >
            Direct Messages
          </Button>
        </div>
      </div>

      {activeTab === 'direct' && <DirectMessages currentUserId={currentUserId} />}

      {activeTab === 'support' && (
      <>
      {/* Search Bar */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search messages by subject, content, or admin reply..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              Found {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} matching &ldquo;{searchQuery}&rdquo;
            </p>
          )}
        </CardContent>
      </Card>

      {/* New Message Form */}
      {showNewMessage && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Send Message to Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <Label>Subject *</Label>
                <Input
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                  placeholder="Brief subject line"
                  required
                />
              </div>

              <div>
                <Label>Message *</Label>
                <div className="relative">
                  <Textarea
                    value={newMessage.message}
                    onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                    placeholder="Describe your question or issue..."
                    rows={6}
                    required
                  />
                  <div className="absolute bottom-2 right-2">
                    <SpeechToTextIcon 
                      onTranscript={(text) => setNewMessage({ ...newMessage, message: newMessage.message + ' ' + text })}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <EmojiPickerButton onEmojiSelect={handleEmojiSelect} />
                </div>
              </div>

              <div>
                <Label>Priority</Label>
                <Select 
                  value={newMessage.priority}
                  onValueChange={(value) => setNewMessage({ ...newMessage, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High Priority</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Attachments (Optional)</Label>
                <FileSelector
                  selectedFiles={selectedFiles}
                  onFilesSelected={setSelectedFiles}
                  multiple={true}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={sending}>
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowNewMessage(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Messages List */}
      {messages.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No messages yet</p>
            <Button onClick={() => setShowNewMessage(true)}>
              Send Your First Message
            </Button>
          </CardContent>
        </Card>
      ) : filteredMessages.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No messages found matching &ldquo;{searchQuery}&rdquo;</p>
            <Button onClick={() => setSearchQuery('')} variant="outline">
              Clear Search
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMessages.map((message) => (
            <Card key={message.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-xl">{message.subject}</CardTitle>
                      {getStatusBadge(message.status)}
                      {getPriorityBadge(message.priority)}
                    </div>
                    <p className="text-sm text-gray-500">
                      Sent {formatDate(message.created_at)}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Original Message - Only show if user initiated the conversation */}
                {message.message && message.status !== 'replied' && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-2">Your Message:</p>
                    <p className="whitespace-pre-wrap text-gray-800">{message.message}</p>
                    <TranslateButton text={message.message} className="mt-2" />
                  </div>
                )}

                {/* Admin Message - Could be reply or initial message from admin */}
                {message.admin_reply && (
                  <div className="bg-cyan-50 rounded-lg p-4 border-l-4 border-cyan-500">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-cyan-600" />
                      <p className="text-sm font-medium text-cyan-800">
                        {message.status === 'replied' && !message.message ? 'Message from Admin:' : 'Admin Reply:'}
                      </p>
                      {message.replied_at && (
                        <p className="text-xs text-gray-500">{formatDate(message.replied_at)}</p>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-gray-800">{message.admin_reply}</p>
                    <TranslateButton text={message.admin_reply} className="mt-2" />
                  </div>
                )}

                {/* Waiting for Reply */}
                {!message.admin_reply && message.status === 'open' && (
                  <div className="text-sm text-gray-500 italic">
                    Waiting for admin response...
                  </div>
                )}

                {/* Attachments */}
                {message.attachments && message.attachments.length > 0 && (
                  <div className="pt-3 border-t">
                    <AttachmentsList attachments={message.attachments} />
                  </div>
                )}

                {/* Emoji Reactions */}
                <div className="pt-3 border-t">
                  <EmojiReactions
                    reactions={message.reactions || {}}
                    onReact={(emoji) => handleReactToMessage(message.id, emoji)}
                    currentUserId={user.id}
                  />
                </div>

                {/* Delete Button */}
                <div className="pt-3 border-t">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteMessage(message.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Message
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {msgTotalPages > 1 && (
        <div className="flex flex-col items-center gap-2 mt-4" data-testid="messages-pagination-wrapper">
          <div className="text-xs text-gray-500">
            Page {msgPage} of {msgTotalPages} · {msgTotal} messages
          </div>
          <Pagination
            page={msgPage}
            totalPages={msgTotalPages}
            onChange={setMsgPage}
            testidPrefix="messages-pagination"
          />
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default MessagesPage;
